import { useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';

import { track } from 'apis/amethyst';
import { trackEvent } from 'helpers/analytics';
import { evFacetClick, evFacetSearch } from 'events/search';
import { toggleSelectedFacetGroup } from 'actions/facets';
import { isSingleSelectFacet } from 'components/search/FacetAccordion/FacetAccordion.utils';
import type { AutoCompleteDataType, FacetDataType } from 'types/search';

interface FacetSelectOptions {
  facetGroup: string;
  facetName: string;
  selectedFacetGroupIndex: number;
  selectedFacetIndex: number;
  isSelected: boolean;
  facetZsoUrl?: string;
}

interface UseFacetSelectionProps {
  autoComplete: AutoCompleteDataType;
  hasAutoComplete?: boolean;
  onFacetSelect: (facetGroup: string, facetName: string, selectedFacetGroupIndex: number, selectedFacetIndex: number) => void;
  getFacetUrlPath: (url?: string) => void;
  getFacetUrlParams: (url?: string) => void;
  facets: FacetDataType[]; // Optional for accordion change logic
}

export const useFacetSelection = ({
  autoComplete,
  hasAutoComplete,
  onFacetSelect,
  getFacetUrlPath,
  getFacetUrlParams,
  facets
}: UseFacetSelectionProps) => {
  const dispatch = useDispatch();

  const handleFacetSelect = (options: FacetSelectOptions) => {
    const { facetGroup, facetName, selectedFacetGroupIndex, selectedFacetIndex, isSelected, facetZsoUrl } = options;

    getFacetUrlParams(facetZsoUrl);
    getFacetUrlPath(facetZsoUrl);
    onFacetSelect(facetGroup, facetName, selectedFacetGroupIndex, selectedFacetIndex);

    // Close accordion for single-select facets after selection
    const currentFacet = facets[selectedFacetGroupIndex];
    if (currentFacet && isSingleSelectFacet(currentFacet) && !isSelected) {
      dispatch(toggleSelectedFacetGroup(facetGroup));
    }

    track(() => [
      evFacetClick,
      {
        facetGroup,
        facetName,
        facetClickSource: 'FACET_ACCORDION',
        selected: !isSelected,
        deselected: !!isSelected
      }
    ]);

    if (hasAutoComplete && autoComplete[facetGroup]?.text) {
      trackEvent('TE_SEARCH_AUTOCOMPLETE', `${facetGroup}:${autoComplete[facetGroup]?.text || ''}`);
    }

    window.scrollTo(0, 0);
  };

  const handleAccordionChange = (expandedValues: string[]) => {
    if (!facets) return;

    // Find which facet group changed by comparing current vs new expanded values
    const currentExpanded = facets.filter(facet => facet.isExpanded).map(facet => facet.facetField);

    // Find the difference to determine which facet was toggled
    const added = expandedValues.find(value => !currentExpanded.includes(value));
    const removed = currentExpanded.find(value => !expandedValues.includes(value));

    const toggledFacet = added || removed;

    if (toggledFacet) {
      dispatch(toggleSelectedFacetGroup(toggledFacet));
    }
  };

  const debouncedSearchEvent = debounce((facetGroup: FacetDataType, searchText: string) => {
    track(() => [
      evFacetSearch,
      {
        autoComplete,
        facetGroup,
        term: searchText
      }
    ]);
  }, 1000);

  return {
    handleFacetSelect,
    handleAccordionChange,
    debouncedSearchEvent
  };
};
